import { useState, useCallback, useEffect } from 'react';
import { X, Shield, CheckCircle, AlertCircle, Loader2, Building2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import axios from 'axios';
import { toast } from 'sonner';

const ABNValidationModal = ({ userId, isOpen, onValidated, onClose, isDark = false }) => {
  const [abn, setAbn] = useState('');
  const [validating, setValidating] = useState(false);
  const [abnResult, setAbnResult] = useState(null);
  const [businessNames, setBusinessNames] = useState([]);
  const [selectedBusinessName, setSelectedBusinessName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showCloseWarning, setShowCloseWarning] = useState(false);

  // iOS-safe body scroll lock
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    }

    return () => {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const formatABN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');

    // Format as XX XXX XXX XXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 2)} ${digits.slice(2)}`;
    if (digits.length <= 8) return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`;
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8, 11)}`;
  };

  const handleABNChange = (e) => {
    const formatted = formatABN(e.target.value);
    setAbn(formatted);
    setError('');
    setAbnResult(null);
    setBusinessNames([]);
    setSelectedBusinessName('');
  };

  const handleValidateABN = useCallback(async () => {
    const abnDigits = abn.replace(/\s/g, '');

    if (abnDigits.length !== 11) {
      setError('ABN must be 11 digits');
      return;
    }

    try {
      setValidating(true);
      setError('');

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/abn/validate`,
        { abn: abnDigits }
      );

      if (response.data.is_valid) {
        setAbnResult(response.data);
        let names = response.data.business_names || [];

        // For Individual/Sole Traders with no trading names, use entity_name
        if (names.length === 0 && response.data.entity_name && response.data.entity_name !== 'Unknown') {
          names = [response.data.entity_name];
        }

        setBusinessNames(names);

        // Auto-select if only one name
        if (names.length === 1) {
          setSelectedBusinessName(names[0]);
        }

        toast.success('ABN validated successfully');
      } else {
        setError(response.data.error || 'ABN validation failed');
      }
    } catch (err) {
      console.error('ABN validation error:', err);
      setError(err.response?.data?.detail || 'Failed to validate ABN. Please check the number and try again.');
    } finally {
      setValidating(false);
    }
  }, [abn]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    if (!selectedBusinessName) {
      setError('Please select a business name');
      return;
    }

    try {
      setSubmitting(true);

      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/${userId}/verify-abn`,
        {
          abn: abn.replace(/\s/g, ''),
          selected_business_name: selectedBusinessName
        }
      );

      if (response.data.success) {
        toast.success('ABN verified! Your profile has been updated.');

        // Update localStorage
        const user = JSON.parse(localStorage.getItem('shootzu_user') || '{}');
        user.abn = response.data.abn;
        user.business_name = response.data.business_name;
        user.gst_registered = response.data.gst_registered;
        user.abn_verified = true;
        user.abn_validated_new_system = true;  // Mark as validated on new system
        localStorage.setItem('shootzu_user', JSON.stringify(user));

        onValidated();
      }
    } catch (err) {
      console.error('ABN verification error:', err);
      setError(err.response?.data?.detail || 'Failed to verify ABN');
    } finally {
      setSubmitting(false);
    }
  }, [abn, selectedBusinessName, userId, onValidated]);

  return (
    <>
    <div
      className="fixed inset-0 flex items-center justify-center z-[200]"
      style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20, overflow: 'hidden', touchAction: 'none' }}
    >
      <div
        className="w-full max-w-lg flex flex-col overflow-hidden"
        style={{
          background: '#12162A',
          border: '1px solid rgba(255,255,255,.12)',
          borderRadius: 22,
          maxHeight: '90vh',
          boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)',
          fontFamily: "'Work Sans', sans-serif",
          touchAction: 'pan-y',
        }}
      >
        {/* Header - More Compact */}
        <div
          className="p-5 relative"
          style={{ background: 'linear-gradient(120deg,#572B7C,#78287C)', borderTopLeftRadius: 22, borderTopRightRadius: 22, overflow: 'hidden' }}
        >
          <img
            src="/assets/website/symbol-white.svg"
            alt=""
            aria-hidden="true"
            style={{ position: 'absolute', right: -24, top: -24, width: 130, height: 130, opacity: 0.06, animation: 'szspin 80s linear infinite', pointerEvents: 'none' }}
          />

          {/* Close button */}
          {onClose && (
            <button
              onClick={() => setShowCloseWarning(true)}
              className="absolute top-3 right-3 flex items-center justify-center transition-all z-10"
              style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(255,255,255,.12)' }}
            >
              <X className="w-5 h-5 text-white" />
            </button>
          )}

          <div className="relative">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center" style={{ width: 44, height: 44, borderRadius: 12, background: 'rgba(255,255,255,.18)' }}>
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  ABN Verification Required
                </h2>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,.8)' }}>
                  Australian Business Register Validation
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content - Scrollable, more compact */}
        <form
          onSubmit={handleSubmit}
          className="p-5 space-y-5 flex-1 overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {/* ABN Input */}
          <div>
            <Label className="mb-2 block" style={{ color: 'rgba(255,255,255,.6)' }}>
              Australian Business Number (ABN) *
            </Label>
            <div className="flex gap-2">
              <Input
                value={abn}
                onChange={handleABNChange}
                placeholder="51 123 456 789"
                maxLength={14}
                className="flex-1 h-12 text-base"
                style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff', fontFamily: "'Roboto Mono', monospace", letterSpacing: '.04em' }}
                disabled={abnResult !== null}
              />
              <Button
                type="button"
                onClick={handleValidateABN}
                disabled={validating || abnResult !== null || abn.replace(/\s/g, '').length !== 11}
                className="h-12 px-6 font-semibold hover:opacity-90"
                style={{ background: '#28C0D0', color: '#06121A' }}
              >
                {validating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : abnResult ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  'Validate'
                )}
              </Button>
            </div>
            <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>
              Enter your 11-digit ABN to verify with the Australian Business Register
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4" style={{ borderRadius: 12, background: 'rgba(185,28,28,.16)', border: '1px solid rgba(185,28,28,.4)' }}>
              <div className="flex gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0" style={{ color: '#F87171' }} />
                <p className="text-sm" style={{ color: '#F87171' }}>
                  {error}
                </p>
              </div>
            </div>
          )}

          {/* Validation Result */}
          {abnResult && (
            <div className="p-4 space-y-3" style={{ borderRadius: 12, background: 'rgba(103,198,180,.1)', border: '1px solid rgba(103,198,180,.34)' }}>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" style={{ color: '#67C6B4' }} />
                <span className="font-semibold" style={{ color: '#67C6B4' }}>
                  ABN Verified with ABR
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>ABN Status</p>
                  <Badge className="mt-1" style={{ background: '#15803D', color: '#fff' }}>
                    {abnResult.abn_status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>GST Registered</p>
                  <Badge className="mt-1" style={{ background: abnResult.gst_registered ? '#28C0D0' : 'rgba(255,255,255,.18)', color: abnResult.gst_registered ? '#06121A' : '#fff' }}>
                    {abnResult.gst_registered ? 'Yes' : 'No'}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,.6)' }}>Entity Type</p>
                  <p className="text-sm font-medium mt-1" style={{ color: '#fff' }}>
                    {abnResult.entity_type}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Business Name Selection */}
          {businessNames.length > 0 && (
            <div>
              <Label className="mb-2 block" style={{ color: 'rgba(255,255,255,.6)' }}>
                Select Your Business Name *
              </Label>
              {businessNames.length === 1 ? (
                <div className="p-4" style={{ borderRadius: 12, background: 'rgba(40,192,208,.08)', border: '1px solid rgba(40,192,208,.28)' }}>
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5" style={{ color: '#28C0D0' }} />
                    <span className="font-semibold" style={{ color: '#fff' }}>
                      {businessNames[0]}
                    </span>
                  </div>
                </div>
              ) : (
                <select
                  value={selectedBusinessName}
                  onChange={(e) => setSelectedBusinessName(e.target.value)}
                  required
                  className="w-full h-12 px-4 text-base"
                  style={{ borderRadius: 11, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.14)', color: '#fff' }}
                >
                  <option value="" style={{ background: '#12162A' }}>-- Select Business Name --</option>
                  {businessNames.map((name, idx) => (
                    <option key={idx} value={name} style={{ background: '#12162A' }}>
                      {name}
                    </option>
                  ))}
                </select>
              )}
              <p className="text-xs mt-1" style={{ color: 'rgba(255,255,255,.4)' }}>
                {businessNames.length === 1
                  ? 'This is the registered business name for your ABN'
                  : 'Multiple business names found - please select the one you use'
                }
              </p>
            </div>
          )}

          {/* Important Notice */}
          <div className="p-4" style={{ borderRadius: 12, background: 'rgba(180,83,9,.18)', border: '1px solid rgba(180,83,9,.4)' }}>
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#FCD34D' }} />
              <div className="text-xs">
                <p className="font-medium mb-1" style={{ color: '#FCD34D' }}>
                  Important:
                </p>
                <ul className="list-disc list-inside space-y-1" style={{ color: 'rgba(255,255,255,.7)' }}>
                  <li>Your business name will be automatically set from ABR records</li>
                  <li>You cannot manually change it - it must match your ABN</li>
                  <li>This ensures compliance with Australian tax regulations</li>
                  <li>This verification is required to continue using the platform</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-3">
            <Button
              type="submit"
              disabled={!abnResult || !selectedBusinessName || submitting}
              className="flex-1 h-12 text-base font-bold disabled:opacity-50 hover:opacity-90"
              style={{ background: '#28C0D0', color: '#06121A' }}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Shield className="w-4 h-4 mr-2" />
                  Verify ABN & Continue
                </>
              )}
            </Button>
          </div>

          {/* Cannot Skip Notice - Compact */}
          <div className="text-center pt-2">
            <p className="text-xs" style={{ color: 'rgba(255,255,255,.4)' }}>
              This verification is mandatory to comply with Australian tax regulations
            </p>
          </div>
        </form>
      </div>
    </div>

    {/* Custom Close Warning Modal */}
    {showCloseWarning && (
      <div
        className="fixed inset-0 flex items-center justify-center z-[300]"
        style={{ background: 'rgba(8,11,20,.7)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: 20 }}
      >
        <div
          className="w-full max-w-md p-6"
          style={{ background: '#12162A', border: '1px solid rgba(255,255,255,.12)', borderRadius: 22, boxShadow: '0 40px 90px -30px rgba(0,0,0,.85)', fontFamily: "'Work Sans', sans-serif" }}
        >
          <div className="flex items-start gap-4 mb-6">
            <div className="flex items-center justify-center flex-shrink-0" style={{ width: 48, height: 48, borderRadius: '9999px', background: 'rgba(180,83,9,.18)', border: '1px solid rgba(180,83,9,.4)' }}>
              <span className="text-2xl">⚠️</span>
            </div>
            <div>
              <h3 className="text-lg font-bold mb-2" style={{ color: '#fff', fontFamily: "'Poppins', sans-serif" }}>
                ABN Verification Required
              </h3>
              <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,.6)' }}>
                This message will keep appearing until you have validated your ABN and Business Details.
              </p>
              <p className="text-sm font-semibold mt-3" style={{ color: '#fff' }}>
                Are you sure you want to close without validating?
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setShowCloseWarning(false)}
              className="flex-1 px-4 py-3 font-bold transition-all"
              style={{ borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,255,255,.14)', color: '#fff' }}
            >
              Go Back
            </button>
            <button
              onClick={() => {
                setShowCloseWarning(false);
                onClose();
              }}
              className="flex-1 px-4 py-3 font-bold transition-all hover:opacity-90"
              style={{ borderRadius: 12, background: 'linear-gradient(120deg,#B45309,#78350F)', color: '#fff' }}
            >
              Close Anyway
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default ABNValidationModal;
